import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const API_URL = process.env.REACT_APP_API_URL;


export function getLevelFromDistrict(district) {
    if (district.startsWith("ΔΙΕΥΘΥΝΣΗ Π.Ε.")) return "Π.Ε.";
    if (district.startsWith("ΔΙΕΥΘΥΝΣΗ Δ.Ε.")) return "Δ.Ε.";
    return "ΑΛΛΟ";
  }

// Δημόσιο/Ιδιωτικό με βάση legal_character (trim/uppercase) + ασφαλές fallback στο name
export function filterBySchoolType(schools, type) {
  if (!type) return schools;

  const isPrivate = (s) => {
    const lc = ((s.legal_character ?? "") + "").trim().toUpperCase();
    if (lc) return lc === "ΙΔΙΩΤΙΚΟ";
    return ((s.name ?? "") + "").toUpperCase().includes("ΙΔΙΩΤΙΚ");
  };

  if (type === "Ιδιωτικά") return schools.filter(isPrivate);
  if (type === "Δημόσια")  return schools.filter((s) => !isPrivate(s));
  return schools;
}



export function getUniqueCategories(schools) {
  return Array.from(new Set(schools.map((s) => s.category))).filter(Boolean);
}




function normalizeGreekCategory(str) {
  return ((str ?? '') + '')
    .toUpperCase()
    // βγάζουμε διαχωριστικά
    .replace(/[.\-–—/]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function isSpecialCategory(cat) {
  const norm = normalizeGreekCategory(cat);
  const tight = norm.replace(/\s+/g, ''); // για ΕΝΕΕΓΥΛ, ΕΕΕΕΚ, ΣΜΕΑΕ

  // Ακρωνύμια & τυποποιήσεις
  if (tight.includes('ΕΕΕΕΚ')) return true;   // Ε.Ε.Ε.Ε.Κ./ΕΕΕΕΚ
  if (tight.includes('ΕΝΕΕΓΥΛ')) return true; // ΕΝ.Ε.Ε.ΓΥ.-Λ./ΕΝΕΕΓΥ-Λ/ΕΝΕΕΓΥΛ
  if (tight.includes('ΣΜΕΑΕ')) return true;   // Σ.Μ.Ε.Α.Ε./ΣΜΕΑΕ

  // Ρητές ονομασίες
  const padded = ` ${norm} `;
  if (padded.includes(' ΕΙΔΙΚΟ ') || padded.includes(' ΕΙΔΙΚΗΣ ') || padded.includes(' ΕΙΔΙΚΩΝ ')) {
    return true; // πιάνει ΕΙΔΙΚΟ ΝΗΠΙΑΓΩΓΕΙΟ/ΔΗΜΟΤΙΚΟ κ.λπ.
  }
  return false;
}

export function orderCategories(cats, level) {
  const L = ((level ?? '') + '').toUpperCase();
  const isPRI = L.includes('ΠΡΩΤΟΒΑΘ') || L.includes('Π.Ε') || L.includes('ΠΕ');
  const isSEC = L.includes('ΔΕΥΤΕΡΟΒΑΘ') || L.includes('Δ.Ε') || L.includes('ΔΕ');

  const PRI = [
    'ΝΗΠΙΑΓΩΓΕΙΟ',
    SPECIAL_CATEGORY_PE_NIPIAGOGEIA,
    'ΔΗΜΟΤΙΚΟ',
    SPECIAL_CATEGORY_PE_DIMOTIKA
  ];
  const SEC = [
    'ΓΥΜΝΑΣΙΟ',
    'ΓΕΝΙΚΟ ΛΥΚΕΙΟ',
    'ΛΥΚΕΙΟ',
    'ΕΠΑΛ',
    'ΕΡΓΑΣΤΗΡΙΑΚΟ ΚΕΝΤΡΟ',
    'ΜΟΥΣΙΚΟ ΣΧΟΛΕΙΟ',
    'ΚΑΛΛΙΤΕΧΝΙΚΟ ΣΧΟΛΕΙΟ',
    'ΣΧΟΛΕΙΟ ΔΕΥΤΕΡΗΣ ΕΥΚΑΙΡΙΑΣ',
    'ΕΝΙΑΙΟ ΕΙΔΙΚΟ ΕΠΑΓΓΕΛΜΑΤΙΚΟ ΓΥΜΝΑΣΙΟ-ΛΥΚΕΙΟ',
  ];

  const base = isPRI ? PRI : (isSEC ? SEC : []);
  const idx = new Map(base.map((v, i) => [v, i]));
  return [...cats].sort((a, b) => {
    const ia = idx.has(a) ? idx.get(a) : 999;
    const ib = idx.has(b) ? idx.get(b) : 999;
    if (ia !== ib) return ia - ib;
    return a.localeCompare(b, 'el');
  });
}


// ✅ Κανoνικοποίηση label κατηγορίας (ώστε να ταιριάζει σε σταθερό σετ)
export function getCategoryKey(cat) {
  const x = ((cat ?? '') + '').trim().toUpperCase().replace(/\s+/g, ' ');
  if (!x) return x;

  // Π.Ε.
  if (x.includes('ΝΗΠ')) return 'ΝΗΠΙΑΓΩΓΕΙΟ';
  if (x.includes('ΔΗΜΟΤ')) return 'ΔΗΜΟΤΙΚΟ';

  // Δ.Ε.
  if (x.includes('ΓΥΜΝ')) return 'ΓΥΜΝΑΣΙΟ';
  if (x.includes('ΓΕΛ') || x.includes('ΓΕΝΙΚΟ ΛΥΚ')) return 'ΓΕΝΙΚΟ ΛΥΚΕΙΟ';
  if (x.includes('ΕΠΑΛ')) return 'ΕΠΑΛ';
  if (x.includes('ΕΡΓΑΣΤΗΡΙΑΚΟ') || x === 'Ε.Κ.' || x.includes(' ΕΚ ')) return 'ΕΡΓΑΣΤΗΡΙΑΚΟ ΚΕΝΤΡΟ';
  if (x.includes('ΜΟΥΣΙΚ')) return 'ΜΟΥΣΙΚΟ ΣΧΟΛΕΙΟ';
  if (x.includes('ΚΑΛΛΙΤΕΧΝ')) return 'ΚΑΛΛΙΤΕΧΝΙΚΟ ΣΧΟΛΕΙΟ';
  if (x.includes('ΔΕΥΤΕΡΗΣ ΕΥΚΑΙΡΙΑΣ')) return 'ΣΧΟΛΕΙΟ ΔΕΥΤΕΡΗΣ ΕΥΚΑΙΡΙΑΣ';
  if (x.includes('ΕΝΙΑΙΟ ΕΙΔΙΚΟ ΕΠΑΓ')) return 'ΕΝΙΑΙΟ ΕΙΔΙΚΟ ΕΠΑΓΓΕΛΜΑΤΙΚΟ ΓΥΜΝΑΣΙΟ-ΛΥΚΕΙΟ';

  return x; // fallback
}

  // ✅ Unique κατηγορίες με βάση την κανονικοποίηση
  export function getUniqueCategoriesCanonical(schools) {
    const set = new Set();
    for (const s of schools) {
      const key = getCategoryKey(s.category);
      if (key) set.add(key);
    }
    return [...set];
  }

  // ✅ Έλεγχος αν είναι ειδικό σχολείο (με βάση το special_type στο JSON)
  export function isSchoolSpecial(school) {
    return Boolean((school?.special_type || "").trim());
  }

  export const CATEGORY_LABELS = {
    'ΝΗΠΙΑΓΩΓΕΙΟ': 'ΝΗΠΙΑΓΩΓΕΙΑ',
    'ΔΗΜΟΤΙΚΟ': 'ΔΗΜΟΤΙΚΑ',
    'ΓΥΜΝΑΣΙΟ': 'ΓΥΜΝΑΣΙΑ',
    'ΓΕΝΙΚΟ ΛΥΚΕΙΟ': 'ΓΕΝΙΚΑ ΛΥΚΕΙΑ',
    'ΛΥΚΕΙΟ': 'ΛΥΚΕΙΑ',
    'ΕΠΑΛ': 'ΕΠΑΛ',
    'ΕΡΓΑΣΤΗΡΙΑΚΟ ΚΕΝΤΡΟ': 'ΕΡΓΑΣΤΗΡΙΑΚΑ ΚΕΝΤΡΑ',
    'ΜΟΥΣΙΚΟ ΣΧΟΛΕΙΟ': 'ΜΟΥΣΙΚΑ ΣΧΟΛΕΙΑ',
    'ΚΑΛΛΙΤΕΧΝΙΚΟ ΣΧΟΛΕΙΟ': 'ΚΑΛΛΙΤΕΧΝΙΚΑ ΣΧΟΛΕΙΑ',
    'ΣΧΟΛΕΙΟ ΔΕΥΤΕΡΗΣ ΕΥΚΑΙΡΙΑΣ': 'ΣΧΟΛΕΙΑ ΔΕΥΤΕΡΗΣ ΕΥΚΑΙΡΙΑΣ',
    'ΕΝΙΑΙΟ ΕΙΔΙΚΟ ΕΠΑΓΓΕΛΜΑΤΙΚΟ ΓΥΜΝΑΣΙΟ-ΛΥΚΕΙΟ': 'ΕΝΙΑΙΑ ΕΙΔΙΚΑ ΕΠΑΓΓΕΛΜΑΤΙΚΑ ΓΥΜΝΑΣΙΑ-ΛΥΚΕΙΑ',
  };

  // ✅ Δώσε σωστό label ειδικού με βάση τη βασική κατηγορία
export function getSpecialCategoryLabel(school) {
  if (!isSchoolSpecial(school)) return null;
  const base = getCategoryKey(school.category);
  if (base === 'ΝΗΠΙΑΓΩΓΕΙΟ') return SPECIAL_CATEGORY_PE_NIPIAGOGEIA;
  if (base === 'ΔΗΜΟΤΙΚΟ')   return SPECIAL_CATEGORY_PE_DIMOTIKA;
  return null; // για Δ.Ε. δεν φτιάχνουμε ξεχωριστά εδώ
}

export function prettyLabel(catKey) {
  return CATEGORY_LABELS[catKey] || catKey;
}

// ✅ Π.Ε. ειδικές υπο-κατηγορίες
export const SPECIAL_CATEGORY_PE_NIPIAGOGEIA = 'ΕΙΔΙΚΑ ΝΗΠΙΑΓΩΓΕΙΑ';
export const SPECIAL_CATEGORY_PE_DIMOTIKA   = 'ΕΙΔΙΚΑ ΔΗΜΟΤΙΚΑ';

// ✅ Δ.Ε. γενική «ομπρέλα» ειδικών
export const SPECIAL_CATEGORY_DE_GENERIC = 'ΕΙΔΙΚΑ ΣΧΟΛΕΙΑ';


  export function applyResultToSchool(schoolsArray, index, result, mode) {
    if (mode === "driving") {
      schoolsArray[index].distanceCar = result.distance;
      schoolsArray[index].durationCar = result.duration;
    } else if (mode === "transit") {
      schoolsArray[index].distanceTransit = result.distance;
      schoolsArray[index].durationTransit = result.duration;
    }
  }


  //----------------------------------------------------------//
  // Create CSV file from the selected schools 
  export function exportSchoolsToCSV(schools, filename = "schools_export.csv") {
  if (!schools.length) return;

  const headers = [
    "Όνομα Σχολείου",
    "Διεύθυνση",
    "Απόσταση (οδήγηση)",
    "Εκτιμώμενος Χρόνος",
  ];

  const rows = schools.map((s) => {
    const dCarVal = s?.durationCar?.value ?? -1;
    const dTraVal = s?.durationInTraffic?.value ?? -1;
    const maxVal = Math.max(dCarVal, dTraVal);
    const text =
      (dTraVal === maxVal && s?.durationInTraffic?.text) ? s.durationInTraffic.text :
      (dCarVal >= 0 && s?.durationCar?.text) ? s.durationCar.text :
      "-";

    return [
      `"${s.name || "-"}"`,
      `"${s.address || "-"}"`,
      `"${s.distanceCar?.text || "-"}"`,
      `"${text}"`,
    ];
  });

  const csvContent =
    [headers, ...rows].map((e) => e.join(",")).join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download",filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

  //----------------------------------------------------------//
  // Create XLSX file from the selected schools 

export function exportSchoolsToExcel(schools) {
  if (!schools.length) return;

  const data = schools.map((s) => {
    const dCarVal = s?.durationCar?.value ?? -1;
    const dTraVal = s?.durationInTraffic?.value ?? -1;
    const maxVal = Math.max(dCarVal, dTraVal);
    const text =
      (dTraVal === maxVal && s?.durationInTraffic?.text) ? s.durationInTraffic.text :
      (dCarVal >= 0 && s?.durationCar?.text) ? s.durationCar.text :
      "-";

    return {
      "Όνομα Σχολείου": s.name || "-",
      "Διεύθυνση": s.address || "-",
      "Απόσταση (οδήγηση)": s.distanceCar?.text || "-",
      "Εκτιμώμενος Χρόνος": text,
    };
  });


  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Σχολεία");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, "schools_export.xlsx");
}


  //----------------------------------------------------------//
  // Call the Google Maps Api from Flask backend 

export async function calculateDistances(originAddress, schools, mode, avoidTolls, setProgress) {
  const chunkSize = 25;
  const updated = [];
  

  const chunks = [];
  for (let i = 0; i < schools.length; i += chunkSize) {
    chunks.push(schools.slice(i, i + chunkSize));
  }

  let completedChunks = 0;

  for (const chunk of chunks) {
    const destinations = chunk.map(s =>
      s.lat && s.lng ? `${s.lat},${s.lng}` : s.address
    );

    const response = await fetch(`${API_URL}/calculate_distances`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        origin: originAddress,
        destinations,
        mode,
        avoid_tolls: avoidTolls,
      }),
    });
    //--------------------
    //For debug
      //console.log(response);
    //--------------------
    const data = await response.json();

    if (data.status !== "OK" || !data.rows || !data.rows[0]) {
      throw new Error(data.error || data.error_message || "API Error");
    }

    const partial = chunk.map((school, i) => ({
      ...school,
      distanceCar: data.rows[0].elements[i]?.distance || null,
      durationCar: data.rows[0].elements[i]?.duration || null,
      durationInTraffic: data.rows[0].elements[i]?.duration_in_traffic || null,
    }));

    updated.push(...partial);

    // ✅ Ενημέρωση προόδου εδώ
    completedChunks++;
    if (setProgress) {
      const progressValue = Math.round((completedChunks / chunks.length) * 100);
      setProgress(progressValue);
    }
  }

  // // 🔽 Ταξινόμηση με βάση το durationInTraffic
  // updated.sort((a, b) => {
  //   const aVal = a.durationInTraffic?.value ?? Infinity;
  //   const bVal = b.durationInTraffic?.value ?? Infinity;
  //   return aVal - bVal;
  // });

  return updated;
}



